import { css } from '@emotion/react'
import { Component, createRef } from 'react'
import { inject, observer } from 'mobx-react'
import { Form } from 'react-final-form'
import arrayMutators from 'final-form-arrays'

import { Drawer, Button, HelpIcon, ProgressBar, TooltipText } from '../../../components'
import { reduceValidationError } from '../../../utils'
import {
  DrawerTitle,
  TopButtonGroup,
  RestoreButtonBox,
  BottomButtonGroup,
  LoadingBox,
} from './EditDrawerStyle'
import MakeChangesContribution from './MakeChangesContribution'
import MakeChangesAdditionalSavings from './MakeChangesAdditionalSavings'
import MakeChangesCompanyStock from './MakeChangesCompanyStock'
import MakeChangesStockOptions from './MakeChangesStockOptions'
import MakeChangesRisk from './MakeChangesRisk'
import MakeChangesRetirementAge from './MakeChangesRetirementAge'
import MakeChangesGoals from './MakeChangesGoals'
import {
  initializeValues,
  restoreRecommendation,
  valuesEqualRecommended,
  valuesEqualModified,
  prepareValuesForSubmit,
  schema,
  calculator,
} from './EditDrawerUtils'
import { MakeChangesProvider } from './MakeChangesContext'
import MakeChangesContext from './MakeChangesContext'

const SCROLL_TOP_OFFSET = 12
const MAX_SCROLL_ATTEMPTS = 20

class EditDrawer extends Component {
  _makeChangesContextValue = {}

  // Track each drawer section so Summary can open at the matching card.
  sectionRefs = {
    retirementAge: createRef(),
    contribution: createRef(),
    additionalSavings: createRef(),
    goals: createRef(),
    companyStock: createRef(),
    stockOptions: createRef(),
    risk: createRef(),
  }

  componentDidMount() {
    // The Summary can mount the drawer already open, so handle the initial scroll.
    if (this.props.active && this.props.scrollToSection) {
      this.scheduleScroll()
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.active && this.props.active && this.props.scrollToSection) {
      this.scheduleScroll()
    }
  }

  componentWillUnmount() {
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf)
  }

  // Scroll before the opening animation finishes to avoid a visible jump.
  scheduleScroll() {
    let attempts = 0
    const tryScroll = () => {
      const container = document.getElementById('drawer')
      const target = this.sectionRefs[this.props.scrollToSection]?.current
      if (container && target) {
        this.scrollToSection()
      } else if (attempts++ < MAX_SCROLL_ATTEMPTS) {
        this.scrollRaf = requestAnimationFrame(tryScroll)
      }
    }
    this.scrollRaf = requestAnimationFrame(tryScroll)
  }

  scrollToSection = () => {
    const target = this.sectionRefs[this.props.scrollToSection]?.current
    const container = document.getElementById('drawer')

    if (!target || !container) {
      return
    }

    const delta = target.getBoundingClientRect().top - container.getBoundingClientRect().top
    container.scrollTop += delta - SCROLL_TOP_OFFSET
  }

  handleCancel = () => {
    this.props.handleCancel()
  }

  validate = values => {
    const { primaryRetAgeEditable, spouseRetAgeEditable } = this.editable()
    const {
      person: { age: primaryAge },
      spouse: { age: spouseAge },
    } = this.props.store

    return schema(
      primaryRetAgeEditable,
      spouseRetAgeEditable,
      Math.min(99, primaryAge + 55),
      spouseRetAgeEditable ? Math.min(99, spouseAge + 55) : 99
    )
      .validate(values, {
        abortEarly: false,
      })
      .then(() => {})
      .catch(err => reduceValidationError(err))
  }

  onSubmit = async formValues => {
    return this.onSubmitWithContext(formValues, this._makeChangesContextValue)
  }

  onSubmitWithContext = async (formValues, contextValue) => {
    if (contextValue?.displayPercentContribLimitExceeded) {
      return
    }

    const { modifiedCase, recommendedCase, getModifiedCase, resetModifiedCase } = this.props.store
    const values = prepareValuesForSubmit({ formValues, modifiedCase, recommendedCase })

    const resetToRecommended = valuesEqualRecommended({ values, recommendedCase })
    const modifyCase = !resetToRecommended && !valuesEqualModified({ values, modifiedCase })

    // Close without recalculating when values are unchanged.
    if (!resetToRecommended && !modifyCase) {
      this.handleCancel()
      return
    }

    // Summary stays mounted during recalculation, so tell it to reload data
    // derived from the active case.
    const runRecalculate = async () => {
      if (resetToRecommended) {
        resetModifiedCase()
      } else {
        await getModifiedCase(values)
      }
      await this.props.onCaseChange?.()
    }

    // Use the host's loading UI when provided; otherwise show the drawer spinner.
    if (this.props.onRecalculate) {
      this.props.onRecalculate(runRecalculate)
      return
    }

    await runRecalculate()
    this.handleCancel()
  }

  editable = () => {
    const {
      recommendedCase,
      modifiedCase,
      person: { includeSpouse },
      active,
      primaryEmployerConfig,
      spouseHasEmployer,
      special457,
      special403b,
      currentGoals,
      features,
    } = this.props.store

    const { primary, spouse, companyStocks, stockOptions } = modifiedCase || recommendedCase

    return {
      primaryEmployer: Boolean(primaryEmployerConfig.rateChangeAllowed && primary && active.id),
      special457: Boolean(!primaryEmployerConfig.rateChangeAllowed && special457 && primary),
      special403b: Boolean(!primaryEmployerConfig.rateChangeAllowed && special403b && primary),
      spouseEmployer: Boolean(includeSpouse && spouseHasEmployer && spouse),
      additionalSavingsIsEditable: true,
      companyStockIsEditable:
        features.companyStockEnabled &&
        companyStocks !== null &&
        companyStocks.filter(companyStock => !companyStock.restricted).length > 0,
      stockOptionsAreEditable:
        features.stockOptionsEnabled &&
        stockOptions !== null &&
        // Show stock options only when they are vested, unexpired, and not underwater.
        stockOptions.filter(
          ({ availableToExercise, isUnderWater }) => availableToExercise && !isUnderWater
        ).length > 0,
      riskIsEditable: true,
      primaryRetAgeEditable: Boolean(primary),
      spouseRetAgeEditable: Boolean(includeSpouse && spouse),
      goalsIsEditable: currentGoals.length > 0,
    }
  }

  render() {
    const { store, active } = this.props
    const {
      account: { annualSalary: primaryAnnualIncome },
      spouse: { annualIncome: spouseAnnualIncome },
      recommendedCase,
      modifiedCase,
      currentGoals,
    } = store
    const editable = this.editable()

    return (
      <Drawer
        active={active}
        width='80%'
        fullMobile
        nopad
        onOverlayClick={this.props.scrollToSection ? this.handleCancel : undefined}>
        <Form
          onSubmit={this.onSubmit}
          decorators={[calculator]}
          mutators={{ ...arrayMutators }}
          initialValues={initializeValues({
            recommendedCase,
            modifiedCase,
            currentGoals,
            primaryAnnualIncome,
            spouseAnnualIncome,
          })}
          validate={this.validate}
          subscription={{ submitting: true, submitError: true, values: true, pristine: true }}
          render={({ handleSubmit, submitting, pristine, values, form }) => (
            <MakeChangesProvider>
              <MakeChangesContext.Consumer>
                {contextValue => {
                  this._makeChangesContextValue = contextValue || {}

                  return (
                    <div
                      css={css`
                        padding: 20px 20px 24px 20px;

                        @media (max-width: 800px) {
                          padding: 1rem 1rem 24px 1rem;
                        }
                      `}>
                      <DrawerTitle>Change strategy details to see potential effects</DrawerTitle>

                      <TopButtonGroup>
                        <RestoreButtonBox>
                          <Button
                            variant='primary'
                            width='100%'
                            onClick={restoreRecommendation({
                              form,
                              recommendedCase,
                              currentGoals,
                              primaryAnnualIncome,
                              spouseAnnualIncome,
                            })}>
                            Restore recommendation
                          </Button>
                        </RestoreButtonBox>
                        <HelpIcon tooltip={TooltipText.restoreRecommendation()} />
                      </TopButtonGroup>

                      {/* Keep the drawer cards in the same order as the Summary sections. */}
                      <div ref={this.sectionRefs.retirementAge}>
                        <MakeChangesRetirementAge store={store} editable={editable} />
                      </div>

                      <div ref={this.sectionRefs.contribution}>
                        <MakeChangesContribution
                          store={store}
                          editable={editable}
                          form={form}
                          values={values}
                        />
                      </div>

                      <div ref={this.sectionRefs.additionalSavings}>
                        <MakeChangesAdditionalSavings store={store} editable={editable} />
                      </div>

                      <div ref={this.sectionRefs.goals}>
                        <MakeChangesGoals store={store} editable={editable} />
                      </div>

                      <div ref={this.sectionRefs.companyStock}>
                        <MakeChangesCompanyStock
                          store={store}
                          editable={editable}
                          values={values}
                        />
                      </div>

                      <div ref={this.sectionRefs.stockOptions}>
                        <MakeChangesStockOptions store={store} editable={editable} />
                      </div>

                      <div ref={this.sectionRefs.risk}>
                        <MakeChangesRisk
                          store={store}
                          editable={editable}
                          form={form}
                          values={values}
                        />
                      </div>

                      <BottomButtonGroup>
                        {submitting && (
                          <LoadingBox>
                            <ProgressBar type='circular' multicolor />
                          </LoadingBox>
                        )}

                        {!submitting && (
                          <>
                            <Button variant='secondary' type='button' onClick={this.handleCancel}>
                              Cancel
                            </Button>

                            <Button
                              variant='primary'
                              type='button'
                              onClick={handleSubmit}
                              disabled={
                                pristine ||
                                !active ||
                                contextValue?.displayPercentContribLimitExceeded
                              }>
                              Recalculate
                            </Button>
                          </>
                        )}
                      </BottomButtonGroup>
                    </div>
                  )
                }}
              </MakeChangesContext.Consumer>
            </MakeChangesProvider>
          )}
        />
      </Drawer>
    )
  }
}

export default inject('store')(observer(EditDrawer))
